import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { siteConfig } from "../Datas/productData";
import "../css/contactus.scss";

const initialFormData = {
  firstName: "",
  lastName: "",
  email: "",
  orderNumber: "",
  subject: "",
  message: "",
};

const subjectOptions = [
  "Product information",
  "Order status",
  "Shipping and delivery",
  "Returns and refunds",
  "Website assistance",
  "Other",
];

const asText = (value) => {
  if (Array.isArray(value)) return value.join(", ");
  if (value && typeof value === "object") {
    return Object.values(value).filter(Boolean).join(", ");
  }
  return value || "";
};

function Contact() {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const submitTimer = useRef(null);
  const successMessage = useRef(null);
  const contact = siteConfig.contact || {};
  const address = asText(contact.address);
  const hours = asText(contact.hours) || "Monday to Friday, 9:00 AM to 6:00 PM";

  useEffect(() => {
    if (showSuccess && successMessage.current) {
      successMessage.current.focus();
    }
  }, [showSuccess]);

  useEffect(
    () => () => {
      if (submitTimer.current) window.clearTimeout(submitTimer.current);
    },
    []
  );

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));

    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: "" }));
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.firstName.trim()) {
      nextErrors.firstName = "Enter your first name.";
    }
    if (!formData.lastName.trim()) {
      nextErrors.lastName = "Enter your last name.";
    }
    if (!formData.email.trim()) {
      nextErrors.email = "Enter your email address.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!formData.subject) {
      nextErrors.subject = "Choose a subject.";
    }
    if (!formData.message.trim()) {
      nextErrors.message = "Tell us how we can help.";
    } else if (formData.message.trim().length < 20) {
      nextErrors.message = "Please include at least 20 characters.";
    }

    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validateForm();

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      const firstInvalidField = Object.keys(nextErrors)[0];
      window.requestAnimationFrame(() => {
        document.getElementById(`contact-${firstInvalidField}`)?.focus();
      });
      return;
    }

    setErrors({});
    setIsSubmitting(true);
    submitTimer.current = window.setTimeout(() => {
      setFormData(initialFormData);
      setIsSubmitting(false);
      setShowSuccess(true);
    }, 900);
  };

  const startNewMessage = () => {
    setShowSuccess(false);
    setErrors({});
  };

  return (
    <main className="contact-page">
      <header className="contact-page__hero">
        <p className="contact-page__eyebrow">Contact</p>
        <h1>Client services</h1>
        <p>
          Our team can assist with the collection, an existing order, delivery,
          or your online experience with {siteConfig.brandName}.
        </p>
      </header>

      <div className="contact-page__layout">
        <aside className="contact-page__details" aria-label="Contact details">
          <div>
            <h2>Speak with us</h2>
            {contact.phone && <a href={`tel:${contact.phone}`}>{contact.phone}</a>}
            {contact.email && (
              <a href={`mailto:${contact.email}`}>{contact.email}</a>
            )}
            <p>{hours}</p>
          </div>

          {address && (
            <div>
              <h2>Correspondence</h2>
              <address>{address}</address>
            </div>
          )}

          <nav aria-label="Service information">
            <h2>Useful information</h2>
            <Link to="/shipping">Shipping and delivery</Link>
            <Link to="/returns">Returns and refunds</Link>
          </nav>
        </aside>

        <section className="contact-page__form-area" aria-labelledby="message-title">
          {!showSuccess ? (
            <>
              <div className="contact-page__form-heading">
                <p className="contact-page__eyebrow">Online enquiry</p>
                <h2 id="message-title">Send a message</h2>
                <p>Required fields are marked with an asterisk.</p>
              </div>

              <form onSubmit={handleSubmit} noValidate>
                <div className="contact-page__field-row">
                  <div className="contact-page__field">
                    <label htmlFor="contact-firstName">First name *</label>
                    <input
                      id="contact-firstName"
                      name="firstName"
                      type="text"
                      autoComplete="given-name"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      aria-invalid={Boolean(errors.firstName)}
                      aria-describedby={errors.firstName ? "contact-firstName-error" : undefined}
                    />
                    {errors.firstName && (
                      <span id="contact-firstName-error" className="contact-page__error">
                        {errors.firstName}
                      </span>
                    )}
                  </div>

                  <div className="contact-page__field">
                    <label htmlFor="contact-lastName">Last name *</label>
                    <input
                      id="contact-lastName"
                      name="lastName"
                      type="text"
                      autoComplete="family-name"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      aria-invalid={Boolean(errors.lastName)}
                      aria-describedby={errors.lastName ? "contact-lastName-error" : undefined}
                    />
                    {errors.lastName && (
                      <span id="contact-lastName-error" className="contact-page__error">
                        {errors.lastName}
                      </span>
                    )}
                  </div>
                </div>

                <div className="contact-page__field-row">
                  <div className="contact-page__field">
                    <label htmlFor="contact-email">Email address *</label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      aria-invalid={Boolean(errors.email)}
                      aria-describedby={errors.email ? "contact-email-error" : undefined}
                    />
                    {errors.email && (
                      <span id="contact-email-error" className="contact-page__error">
                        {errors.email}
                      </span>
                    )}
                  </div>

                  <div className="contact-page__field">
                    <label htmlFor="contact-orderNumber">Order number</label>
                    <input
                      id="contact-orderNumber"
                      name="orderNumber"
                      type="text"
                      autoComplete="off"
                      value={formData.orderNumber}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="contact-page__field">
                  <label htmlFor="contact-subject">Subject *</label>
                  <select
                    id="contact-subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    aria-invalid={Boolean(errors.subject)}
                    aria-describedby={errors.subject ? "contact-subject-error" : undefined}
                  >
                    <option value="">Select a subject</option>
                    {subjectOptions.map((subject) => (
                      <option key={subject} value={subject}>
                        {subject}
                      </option>
                    ))}
                  </select>
                  {errors.subject && (
                    <span id="contact-subject-error" className="contact-page__error">
                      {errors.subject}
                    </span>
                  )}
                </div>

                <div className="contact-page__field">
                  <label htmlFor="contact-message">Message *</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows="7"
                    maxLength="1200"
                    value={formData.message}
                    onChange={handleInputChange}
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={errors.message ? "contact-message-error" : "contact-message-count"}
                  />
                  <span id="contact-message-count" className="contact-page__count">
                    {formData.message.length} / 1200
                  </span>
                  {errors.message && (
                    <span id="contact-message-error" className="contact-page__error">
                      {errors.message}
                    </span>
                  )}
                </div>

                <button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Sending..." : "Send enquiry"}
                </button>
              </form>
            </>
          ) : (
            <div
              className="contact-page__success"
              ref={successMessage}
              role="status"
              tabIndex="-1"
            >
              <span aria-hidden="true">01</span>
              <p className="contact-page__eyebrow">Message received</p>
              <h2 id="message-title">Thank you for contacting us.</h2>
              <p>
                A client advisor will review your enquiry and respond using the
                contact details provided.
              </p>
              <button type="button" onClick={startNewMessage}>
                Send another enquiry
              </button>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

export default Contact;
